import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Cached as CacheIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import apiService from '../services/api.service';

interface MonitorData {
  status: string;
  timestamp: string;
  server: {
    uptime: number;
    uptimeFormatted: string;
    pid: number;
    nodeVersion: string;
    platform: string;
    environment: string;
  };
  memory: {
    heapUsed: string;
    heapTotal: string;
    heapPercentage: string;
    rss: string;
    external: string;
  };
  database: {
    activeConnections: number;
    maxConnections: number | string;
    idleConnections: number | string;
    waitingClients: number;
  };
  cache: {
    size: number;
    entries: Array<{ key: string; age: number }>;
  };
  process: {
    cpuUsage: { user: number; system: number };
    activeHandles: number;
    activeRequests: number;
  };
}

const SystemMonitor: React.FC = () => {
  const [data, setData] = useState<MonitorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchMonitorData = async () => {
    try {
      const response: any = await apiService.get('/monitor');
      setData(response.data);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitorData();
    const interval = setInterval(fetchMonitorData, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error || 'No monitoring data available'}
        </Alert>
      </Box>
    );
  }

  const heapPercent = parseInt(data.memory.heapPercentage.replace('%', ''));
  const getHealthColor = (percent: number) => {
    if (percent < 60) return 'success';
    if (percent < 80) return 'warning';
    return 'error';
  };

  return (
    <Box component="section" aria-labelledby="sm-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={{ xs: 1.5, sm: 0 }}
        mb={3}
      >
        <Typography
          component="h1"
          id="sm-heading"
          variant="h5"
          fontWeight="bold"
          sx={{ fontSize: { xs: 18, sm: 22, md: 24 } }}
        >
          System Monitor
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            icon={<ClockIcon />}
            label={`Updated: ${lastUpdate.toLocaleTimeString()}`}
            size="small"
            color="primary"
          />
          <Chip
            label={data.status.toUpperCase()}
            color={data.status === 'online' ? 'success' : 'error'}
            size="small"
          />
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* Server Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ComputerIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Server Status</Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Uptime:</Typography>
                  <Typography variant="body2" fontWeight="bold">{data.server.uptimeFormatted}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Environment:</Typography>
                  <Chip label={data.server.environment} size="small" color="info" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Node Version:</Typography>
                  <Typography variant="body2">{data.server.nodeVersion}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Process ID:</Typography>
                  <Typography variant="body2">{data.server.pid}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Memory Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <MemoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Memory Usage</Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">Heap:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.memory.heapUsed} / {data.memory.heapTotal}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={heapPercent}
                    color={getHealthColor(heapPercent)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" align="center" display="block" mt={0.5}>
                    {data.memory.heapPercentage}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">RSS:</Typography>
                  <Typography variant="body2">{data.memory.rss}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">External:</Typography>
                  <Typography variant="body2">{data.memory.external}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Connections */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <StorageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Database Connections</Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Active:</Typography>
                  <Chip
                    label={data.database.activeConnections}
                    size="small"
                    color={data.database.activeConnections > 50 ? 'warning' : 'success'}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Idle:</Typography>
                  <Typography variant="body2">{data.database.idleConnections}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Max:</Typography>
                  <Typography variant="body2">{data.database.maxConnections}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Waiting Clients:</Typography>
                  <Chip
                    label={data.database.waitingClients}
                    size="small"
                    color={data.database.waitingClients > 0 ? 'error' : 'success'}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Cache Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CacheIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Response Cache</Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Cached Entries:</Typography>
                  <Chip label={data.cache.size} size="small" color="info" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Max Size:</Typography>
                  <Typography variant="body2">2000</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>Cache Usage:</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(data.cache.size / 2000) * 100}
                    color="info"
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary" align="center" display="block" mt={0.5}>
                    {Math.round((data.cache.size / 2000) * 100)}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Process Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SpeedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Process Activity</Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Active Requests:</Typography>
                  <Chip
                    label={data.process.activeRequests}
                    size="small"
                    color={data.process.activeRequests > 10 ? 'warning' : 'success'}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Active Handles:</Typography>
                  <Typography variant="body2">{data.process.activeHandles}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">CPU User:</Typography>
                  <Typography variant="body2">{Math.round(data.process.cpuUsage.user / 1000)}ms</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">CPU System:</Typography>
                  <Typography variant="body2">{Math.round(data.process.cpuUsage.system / 1000)}ms</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Health Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Health Summary</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Alert
                  severity={heapPercent < 80 ? 'success' : 'warning'}
                  icon={false}
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2">
                    <strong>Memory:</strong> {heapPercent < 80 ? 'Healthy' : 'High Usage'}
                  </Typography>
                </Alert>
                <Alert
                  severity={data.database.activeConnections < 50 ? 'success' : 'warning'}
                  icon={false}
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2">
                    <strong>Database:</strong> {data.database.activeConnections < 50 ? 'Normal' : 'High Load'}
                  </Typography>
                </Alert>
                <Alert
                  severity={data.cache.size < 1500 ? 'success' : 'info'}
                  icon={false}
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2">
                    <strong>Cache:</strong> {data.cache.size < 1500 ? 'Optimal' : 'Near Capacity'}
                  </Typography>
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Alert severity="info">
          Auto-refreshes every 5 seconds. Monitor CPU, memory, and database usage to ensure optimal performance.
        </Alert>
      </Box>
    </Box>
  );
};

export default SystemMonitor;
